/**
 * CalendarGrid - Graella completa del calendari
 * Combina: Headers, Rooms, Days, i Reservations
 *
 * Mobile-first, modular, touch-friendly
 */

import { useRef, useCallback, useMemo, type MouseEvent, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CalendarHeaderCell, type DayEvent } from './CalendarHeaderCell';
import { CalendarRoomCell, type RoomInfo } from './CalendarRoomCell';
import { ROOM_COLUMN_WIDTH } from './room-column';
import { CalendarDayCell } from './CalendarDayCell';
import { type ReservationCellData } from './ReservationCell';
import { ReservationPopover } from './ReservationPopover';
import { CALENDAR_PALETTE, useCalendarTheme } from './calendar-theme';
import { stayCornerStyle } from './calendar-radius';
import { CancelledStrip } from './grid/CancelledStrip';
import { isWeekendNight } from './grid/day-labels';
import {
  calculateReservationPosition,
  cardDense,
  cardLightSize,
  cardLines,
  clipRoundingClasses,
  computeClip,
  computeRowLayout,
  resolveDayClick,
  rowSlotStyle,
  visibleRangeOf,
  type RowLayout,
  type RowSlotStyle,
  type VisibleRange,
} from './grid/row-layout';
import { StayChangeConfirmDialog } from './StayChangeConfirmDialog';
import { DragGhost } from './grid/DragGhost';
import { GRID_DAYS_ATTR, GRID_ROW_UNIT_ATTR, useReservationDrag } from './grid/useReservationDrag';
import { isOtaChannel } from '@/components/reservation-sheet/EditStayDialog';
import {
  GRID_RESERVATION_ID_ATTR,
  GRID_TARGET_ATTR,
  isTapGesture,
  TAP_MAX_MOVE_PX,
  useGridPointer,
  type DragMode,
} from './grid/useGridPointer';

// ============================================
// TYPES
// ============================================

/**
 * Un bloc de files amb capçalera pròpia (mode «Totes» del calendari v2: un bloc per propietat).
 * Totes comparteixen la capçalera de dates i el scroll horitzontal.
 */
export interface CalendarGridSection {
  /** Clau estable del bloc (la propietat de Beds24). */
  id: string;
  /** El que surt a la fila separadora (el nom de la propietat). */
  label: string;
  rooms: RoomInfo[];
  /** Les dades del bloc encara arriben: «Carregant…» a la fila separadora. */
  loading?: boolean;
  /** Una query del bloc ha fallat: «Error carregant» i el botó de reintentar a la fila separadora. */
  error?: boolean;
  onRetry?: () => void;
  /** Text quan el bloc no té cap habitació (i no carrega). */
  emptyLabel?: string;
}

export interface CalendarGridProps {
  dates: Date[];
  /** Les files d'una sola propietat. Amb `sections`, no es fa servir. */
  rooms?: RoomInfo[];
  /**
   * Mode «Totes»: blocs de files, un per propietat, amb una fila separadora amb el nom. Una
   * reserva no es pot arrossegar a un bloc que no és el seu (moure entre propietats, a Beds24).
   */
  sections?: CalendarGridSection[];
  reservations: ReservationCellData[];
  dayEvents?: Record<string, DayEvent[]>; // key = YYYY-MM-DD
  selectedReservationId?: string | null; // Per marcar la reserva seleccionada
  onReservationClick?: (id: string) => void;
  onDayClick?: (date: Date, room: RoomInfo) => void;
  onRoomClick?: (room: RoomInfo) => void;
  className?: string;
  cellWidth?: number;
  cellHeight?: number;
  /**
   * Qui pinta la card d'una reserva dins del seu forat ja posicionat.
   *
   * Per defecte és el `ReservationPopover` del calendari v2, amb tot el que porta
   * (pagaments, llits, WhatsApp). El **calendari històric** hi passa la seva pròpia card de
   * només lectura (`plans/legacy-calendar-readonly.md` D5): la graella només sap col·locar
   * rectangles, i qui hi va a dins és cosa de la pàgina.
   */
  renderReservation?: (reservation: ReservationCellData, ctx: { isSelected: boolean }) => ReactNode;
  /**
   * `calendarV2_settings.dragMode` (runbook 7, decisió 7). `mouse` (per defecte) = amb el ratolí
   * es mou una reserva de fila i s'allarga o s'escurça estirant la vora dreta, sempre amb
   * confirmació; `off` = ni nansa ni arrossegament. Amb `renderReservation` (calendari històric,
   * només lectura) no s'arrossega mai.
   */
  dragMode?: DragMode;
  /**
   * Ressaltat del grup (`plans/calendar-card-signals.md` §6 F1.11): els ids del conjunt porten l'anell
   * de selecció i la resta de cards queden a `opacity: 0.35`. Absent o buit = cap ressaltat.
   */
  highlightIds?: ReadonlySet<string>;
}

// ============================================
// HELPERS
// ============================================

function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}


function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Alçada fixa de la fila de capçalera de dates (px). Els separadors de propietat (vista «Totes»)
 * s'hi enganxen a sota (`top`).
 */
export const GRID_HEADER_HEIGHT = 76;

/** Capes (z-index) de la graella, de dalt a baix. */
const Z_CORNER = 'z-40';
const Z_HEADER = 'z-30';
/** Separador de propietat enganxat: per sota de la capçalera, per sobre de les cards (z-20). */
const Z_SECTION_HEADER = 'z-[24]';

/** Fons de la fila separadora de propietat (vista «Totes»). */
const SECTION_BG = CALENDAR_PALETTE.headerWeekend;

// El llindar tap/arrossegament i `isTapGesture` viuen ara a `grid/useGridPointer.ts` (F2).
export { isTapGesture, TAP_MAX_MOVE_PX };

// La posició horitzontal de cada reserva (arribades i sortides a mig dia, D4 / F6) és
// `calculateReservationPosition`, a `grid/row-layout.ts` (amb tests).

// ============================================
// COMPONENT
// ============================================

export function CalendarGrid({
  dates,
  rooms: roomsProp,
  sections,
  reservations,
  dayEvents = {},
  selectedReservationId,
  onReservationClick,
  onDayClick,
  onRoomClick,
  className,
  cellWidth = 200,
  cellHeight = 70,
  renderReservation,
  dragMode = 'mouse',
  highlightIds,
}: CalendarGridProps) {
  const { cardRadius, rightRadius } = useCalendarTheme();
  // Ressaltat del grup (§6 F1.11): només si el conjunt porta algun id.
  const highlighting = !!highlightIds && highlightIds.size > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  // Amb blocs, les files són les de tots els blocs en ordre, i cada unitat sap de quin bloc és.
  const rooms = useMemo(
    () => (sections ? sections.flatMap((section) => section.rooms) : (roomsProp ?? [])),
    [sections, roomsProp],
  );
  const rowGroupOf = useMemo(() => {
    if (!sections) return undefined;
    const map = new Map<string, string>();
    for (const section of sections) {
      for (const room of section.rooms) map.set(room.id, section.id);
    }
    return map;
  }, [sections]);

  // F3: moure de fila i allargar. Mai al calendari històric (només lectura).
  const dragEnabled = dragMode === 'mouse' && !renderReservation;
  const gridStart = dates.length > 0 ? formatDateKey(dates[0]) : null;
  const reservationDrag = useReservationDrag({
    containerRef,
    rowsRef,
    rooms,
    reservations,
    gridStart,
    cellWidth,
    rowGroupOf,
    // Les sortides són a mig dia (D4): la frontera d'estirar la vora dreta també.
    checkOutOffsetX: cellWidth / 2,
  });
  const { drag, pendingChange, clearPending, ghostRef, cancelGestureRef } = reservationDrag;

  // Un sol router de Pointer Events (runbook 7, F2): scroll, tap, moure de fila i allargar.
  const { handlers, touchAction, getLastGesture, isDragging, cancel } = useGridPointer({
    containerRef,
    dragMode: dragEnabled ? 'mouse' : 'off',
    moveRow: dragEnabled ? reservationDrag.moveRow : undefined,
    resize: dragEnabled ? reservationDrag.resize : undefined,
  });
  cancelGestureRef.current = cancel;

  // Tap a una cel·la buida → formulari de nova reserva; arrossegar el fons no l'obre.
  // Amb les cards a mig dia (D4), la meitat esquerra de la columna és la nit d'abans i la dreta, la
  // del dia (`resolveDayClick`): la meitat lliure del dia de sortida d'algú crea una entrada aquell
  // dia, i la meitat lliure del dia d'arribada d'algú, una entrada el dia abans (no xoca).
  const handleDayCellClick = useCallback(
    (date: Date, room: RoomInfo, event: MouseEvent<HTMLDivElement>) => {
      if (!getLastGesture().wasTap) return;
      const rect = event.currentTarget.getBoundingClientRect();
      onDayClick?.(resolveDayClick(date, event.clientX - rect.left, rect.width), room);
    },
    [onDayClick, getLastGesture],
  );

  // ============================================
  // RESERVATION POSITIONING
  // ============================================

  // Rang visible: primer dia i l'endemà de l'últim.
  const visibleRange = useMemo(
    () =>
      visibleRangeOf(
        dates.length > 0 ? formatDateKey(dates[0]) : null,
        dates.length > 0 ? formatDateKey(dates[dates.length - 1]) : null,
      ),
    [dates],
  );

  // Reserves per fila (unitId), amb el lloc de cada una dins de la fila (cancel·lades a la franja
  // de baix, D6) i els camps de presentació ja posats per a la card: vores tallades
  // (`clipStart`/`clipEnd`, P1), línies i densitat segons l'espai del seu lloc (`lines`/`dense`, F7) i
  // la mida dels llums (`lightSize`, senyals §6 F1.3).
  const rowsData = useMemo(() => {
    const raw = new Map<string, ReservationCellData[]>();
    reservations.forEach((res) => {
      const roomId = res.unitId;
      if (!roomId) return;
      const existing = raw.get(roomId) || [];
      existing.push(res);
      raw.set(roomId, existing);
    });
    const byRoom = new Map<string, ReservationCellData[]>();
    const layouts = new Map<string, RowLayout>();
    raw.forEach((list, roomId) => {
      if (!visibleRange) {
        byRoom.set(roomId, list);
        return;
      }
      const layout = computeRowLayout(list, visibleRange);
      layouts.set(roomId, layout);
      byRoom.set(
        roomId,
        list.map((res) => {
          const slot = layout.slots.get(res.id) ?? 'full';
          return {
            ...res,
            ...computeClip(res, visibleRange),
            lines: cardLines(cellHeight, slot),
            dense: cardDense(cellHeight, slot),
            lightSize: cardLightSize(cellHeight, slot),
          };
        }),
      );
    });
    return { byRoom, layouts };
  }, [reservations, visibleRange, cellHeight]);

  // Columna d'avui (-1 si no es veu): línia vertical de 2 px de la capçalera fins a baix.
  const todayIndex = dates.findIndex((date) => isToday(date));
  const todayLineLeft = todayIndex >= 0 ? todayIndex * cellWidth : null;

  // ============================================
  // RENDER
  // ============================================

  const totalWidth = dates.length * cellWidth + ROOM_COLUMN_WIDTH;

  // Una fila d'habitació: cel·la del nom, cel·les de dies i reserves posicionades.
  const renderRoomRow = (room: RoomInfo) => {
    const roomReservations = rowsData.byRoom.get(room.id) || [];
    const rowLayout = rowsData.layouts.get(room.id);
    // Les cancel·lades es pinten primer: per sota de les vives (que mai no queden tapades).
    const cancelledReservations = rowLayout?.hasCancelledStrip
      ? roomReservations.filter((r) => rowLayout.slots.get(r.id) === 'cancelled')
      : [];
    const liveReservations = rowLayout?.hasCancelledStrip
      ? roomReservations.filter((r) => rowLayout.slots.get(r.id) !== 'cancelled')
      : roomReservations;
    // Fila de destí d'un «moure»: verda si és lliure, vermella si xoca.
    const isDropTarget =
      drag?.kind === 'move' &&
      drag.targetUnitId === room.id &&
      drag.targetUnitId !== drag.fromUnitId;
    // Fila d'una altra propietat sota el punter (mode «Totes»): vermella, perquè es vegi abans de
    // deixar anar que aquí no es pot (moure entre propietats, a Beds24).
    const isBlockedTarget =
      drag?.kind === 'move' && !!drag.crossGroup && drag.blockedUnitId === room.id;

    return (
      <div
        key={room.id}
        {...{ [GRID_ROW_UNIT_ATTR]: room.id }}
        className="group/row flex border-b"
        style={{ borderBottomColor: CALENDAR_PALETTE.separator }}
      >
        {/* Room Cell - sticky left */}
        <CalendarRoomCell room={room} onClick={onRoomClick} />

        {/* Days Container */}
        <div
          className="relative flex-1"
          style={{ height: cellHeight }}
          {...{ [GRID_DAYS_ATTR]: '' }}
        >
          {isBlockedTarget && (
            <div
              aria-hidden="true"
              data-testid="drop-blocked"
              title="Per moure una reserva a una altra propietat, fes-ho a Beds24"
              className="pointer-events-none absolute inset-0 z-[5] bg-red-100/70 ring-2 ring-inset ring-red-500"
            />
          )}
          {isDropTarget && (
            <div
              aria-hidden="true"
              data-testid="drop-target"
              className={cn(
                'pointer-events-none absolute inset-0 z-[5] ring-2 ring-inset',
                drag.collision
                  ? 'bg-red-100/70 ring-red-500'
                  : 'bg-green-100/70 ring-green-500',
              )}
            />
          )}
          {/* Day Cells (background) */}
          {dates.map((date, dayIndex) => (
            <CalendarDayCell
              key={dayIndex}
              date={date}
              isToday={dayIndex === todayIndex}
              isWeekend={isWeekendNight(date)}
              onClick={onDayClick ? (_day, event) => handleDayCellClick(date, room, event) : undefined}
              style={{
                left: dayIndex * cellWidth,
                width: cellWidth,
                height: cellHeight,
                top: 0,
              }}
            />
          ))}

          {/* Avui: per sobre de les cel·les i per sota de les cards (cap card queda tapada). */}
          {todayLineLeft !== null && <TodayLine left={todayLineLeft} className="top-0 -bottom-px" />}

          {/* Cancel·lades (D6): franja baixa de la fila (`rowSlotStyle`); no s'arrosseguen. */}
          {cancelledReservations.map((reservation) => {
            const pos = visibleRange
              ? calculateReservationPosition(
                  reservation.checkIn,
                  reservation.checkOut,
                  visibleRange,
                  cellWidth,
                )
              : null;
            if (!pos) return null;
            return (
              <CancelledStrip
                key={reservation.id}
                reservation={reservation}
                left={pos.left}
                width={pos.width}
                slotStyle={rowSlotStyle(cellHeight, 'cancelled')}
                onOpenSheet={onReservationClick}
                getLastGesture={getLastGesture}
              />
            );
          })}

          {/* Reservations (positioned absolutely) */}
          {liveReservations.map((reservation) => {
            const pos = visibleRange
              ? calculateReservationPosition(
                  reservation.checkIn,
                  reservation.checkOut,
                  visibleRange,
                  cellWidth,
                )
              : null;

            // Skip if outside visible range
            if (!pos) return null;

            const isSelected = reservation.id === selectedReservationId;
            const isCancelled = reservation.status === 'cancelled';
            // Bloquejada a l'habitació (`plans/reservation-move-lock.md` §4 F.3, L2): no es pot
            // arrossegar a una altra fila, però sí obrir amb un clic.
            const canMove =
              dragEnabled && !isCancelled && !reservation.signals?.moveLock?.reason;
            // Allargar o escurçar: només directes (les OTA es canvien a l'extranet del canal), i
            // només si el mig dia de la sortida es veu (sense `clipEnd`): la vora que s'estira és
            // la sortida de debò, no la vora de la graella. Només canvia dates: també les
            // bloquejades (L3), per això no depèn de `canMove`.
            const canResize =
              dragEnabled &&
              !isCancelled &&
              !isOtaChannel(reservation.channel) &&
              !reservation.clipEnd;
            const isMoving =
              drag?.kind === 'move' && drag.reservationId === reservation.id;
            const resizing =
              drag?.kind === 'resize' && drag.reservationId === reservation.id
                ? drag
                : null;
            // Mentre s'estira, la mateixa geometria amb la sortida nova (mig dia, retallada a la
            // vora de la graella si en surt).
            const width =
              resizing && visibleRange
                ? (calculateReservationPosition(
                    reservation.checkIn,
                    resizing.newCheckOut,
                    visibleRange,
                    cellWidth,
                  )?.width ?? pos.width)
                : pos.width;
            // Els marcs que envolten la card (selecció, estirar, nansa) casen amb les seves vores
            // tallades i amb el radi de cada costat (el dret, recte per defecte, D3).
            const clipRounding = clipRoundingClasses(reservation);
            const corners = stayCornerStyle(cardRadius, rightRadius, reservation);
            // Lloc vertical (F7: px segons l'alçada de la fila); els marcs de selecció i d'estirar
            // són d'aquest mateix element o de la card de dins, i el segueixen.
            const slotStyle = rowSlotStyle(cellHeight, rowLayout?.slots.get(reservation.id) ?? 'full');
            // Ressaltat del grup (§6 F1.11): anell de selecció als del conjunt, la resta atenuades.
            const isHighlighted = highlighting && highlightIds!.has(reservation.id);
            const isDimmed = highlighting && !isHighlighted && !isMoving;
            const ringed = isSelected || isHighlighted;
            // Senyals (§6 F1.4-F1.5): la card sap la seva amplada (llums de les cards estretes) i si
            // la nansa d'estirar li tapa els últims 8 px.
            const cellData: ReservationCellData = {
              ...reservation,
              cardWidth: width,
              resizeHandle: canResize,
            };

            return (
              <div
                key={reservation.id}
                // `useGridPointer` sap on comença el gest per aquests atributs: el cos és
                // `reservation` (moure de fila) només si es pot moure; la nansa, `edge`.
                {...{
                  [GRID_TARGET_ATTR]: canMove ? 'reservation' : undefined,
                  [GRID_RESERVATION_ID_ATTR]: reservation.id,
                }}
                data-highlight={highlighting ? (isHighlighted ? 'on' : 'dim') : undefined}
                className={cn(
                  'absolute',
                  !drag && 'transition-all duration-200',
                  ringed && 'z-20',
                  // L'original es queda al seu lloc, atenuat, fins a confirmar.
                  isMoving && 'opacity-40',
                  resizing && 'z-20 rounded-lg ring-2',
                  resizing && clipRounding,
                  resizing && (resizing.collision ? 'ring-red-500' : 'ring-green-500'),
                )}
                style={{
                  left: pos.left,
                  width,
                  ...slotStyle,
                  ...(resizing ? corners : undefined),
                  ...(isDimmed ? { opacity: 0.35 } : undefined),
                }}
              >
                {renderReservation ? (
                  renderReservation(reservation, { isSelected })
                ) : (
                  <ReservationPopover
                    data={cellData}
                    onOpenSheet={onReservationClick}
                    getLastGesture={getLastGesture}
                    hoverDisabled={isDragging}
                    style={ringed ? corners : undefined}
                    className={cn(
                      ringed &&
                        'ring-2 ring-blue-500 ring-offset-1 shadow-lg rounded-lg',
                      ringed && clipRounding,
                    )}
                  />
                )}
                {canResize && (
                  <div
                    aria-hidden="true"
                    data-testid="resize-handle"
                    {...{ [GRID_TARGET_ATTR]: 'edge' }}
                    className="absolute inset-y-0 right-0 z-[1] w-2 cursor-ew-resize hover:bg-black/20"
                    style={{
                      borderTopRightRadius: corners.borderTopRightRadius,
                      borderBottomRightRadius: corners.borderBottomRightRadius,
                    }}
                  />
                )}
              </div>
            );
          })}

          {/* Fantasma del «moure»: mateixa columna, segueix el punter en vertical. */}
          {drag?.kind === 'move' && drag.fromUnitId === room.id && (
            <MoveGhost
              reservation={roomReservations.find((r) => r.id === drag.reservationId)}
              range={visibleRange}
              cellWidth={cellWidth}
              slotStyle={rowSlotStyle(
                cellHeight,
                rowLayout?.slots.get(drag.reservationId) === 'live' ? 'live' : 'full',
              )}
              collision={drag.collision || !!drag.crossGroup}
              ghostRef={ghostRef}
            />
          )}
        </div>
      </div>
    );
  };


  return (
    <>
      <div
        ref={containerRef}
        {...handlers}
        className={cn(
          // P26: cap vora gruixuda ni padding per sobre de la capçalera enganxada (`top-0` és
          // la vora de dalt de l'àrea de scroll); 1 px de vora i cantonades poc arrodonides.
          'relative border rounded-lg shadow-sm p-0',
          'overflow-x-auto overflow-y-auto overscroll-x-contain h-full select-none',
          'cursor-grab active:cursor-grabbing',
          className,
        )}
        style={{
          WebkitOverflowScrolling: 'touch',
          touchAction,
          borderColor: CALENDAR_PALETTE.separator,
          backgroundColor: CALENDAR_PALETTE.cell,
        }}
      >
        <div style={{ minWidth: totalWidth }}>
          {/* ============================================ */}
          {/* HEADER ROW - STICKY TOP + LEFT CORNER */}
          {/* ============================================ */}
          <div
            data-testid="grid-header"
            className={cn('sticky top-0 flex border-b-2 shadow-sm', Z_HEADER)}
            style={{
              height: GRID_HEADER_HEIGHT,
              backgroundColor: CALENDAR_PALETTE.header,
              borderBottomColor: CALENDAR_PALETTE.separator,
            }}
          >
            {/* Corner Cell - sticky left, amplada fixa de la columna d'habitacions (F7) */}
            <div
              className={cn(
                'sticky left-0 shrink-0 p-2 border-r-2 flex items-center justify-center',
                Z_CORNER,
              )}
              style={{
                width: ROOM_COLUMN_WIDTH,
                minWidth: ROOM_COLUMN_WIDTH,
                backgroundColor: CALENDAR_PALETTE.header,
                borderRightColor: CALENDAR_PALETTE.separator,
              }}
              title="Habitació"
              data-testid="grid-corner"
            >
              <span className="text-lg" aria-hidden="true">🏠</span>
              <span className="sr-only">Habitació</span>
            </div>

            {/* Day Headers */}
            {dates.map((date, index) => (
              <CalendarHeaderCell
                key={index}
                date={date}
                isToday={index === todayIndex}
                isWeekend={isWeekendNight(date)}
                events={dayEvents[formatDateKey(date)]}
                onClick={onDayClick ? () => {} : undefined}
                cellWidth={cellWidth}
              />
            ))}
          </div>

          {/* ============================================ */}
          {/* ROOM ROWS */}
          {/* ============================================ */}
          <div ref={rowsRef}>
            {sections
              ? sections.map((section) => (
                  <div key={section.id}>
                    <SectionHeaderRow
                      todayLineLeft={todayLineLeft}
                      label={section.label}
                      loading={section.loading}
                      error={section.error}
                      onRetry={section.onRetry}
                      empty={section.rooms.length === 0}
                      emptyLabel={section.emptyLabel}
                    />
                    {section.rooms.map(renderRoomRow)}
                  </div>
                ))
              : rooms.map(renderRoomRow)}
          </div>
        </div>
      </div>

      {/* Res s'escriu en deixar anar: sempre aquesta confirmació (decisió 5). */}
      <StayChangeConfirmDialog change={pendingChange} onClose={clearPending} />
    </>
  );
}

/**
 * La línia vertical d'avui dins d'una fila (2 px, `todayLine`), a la vora esquerra de la columna.
 * Cada fila en pinta el seu tros; `-bottom-px` / `-bottom-0.5` la fa passar per sobre de la vora
 * de sota de la fila, perquè de la capçalera fins a baix es vegi sòlida.
 */
function TodayLine({ left, className }: { left: number; className?: string }) {
  return (
    <div
      aria-hidden="true"
      data-testid="today-line"
      className={cn('pointer-events-none absolute w-0.5', className)}
      style={{ left, backgroundColor: CALENDAR_PALETTE.todayLine }}
    />
  );
}

/**
 * La fila separadora d'un bloc (mode «Totes»): el nom de la propietat, enganxat a l'esquerra
 * perquè es llegeixi amb el scroll horitzontal, i a dalt (P11) just a sota de la capçalera de
 * dates mentre es recorre el bloc. No és destí de cap gest (no porta `GRID_ROW_UNIT_ATTR` ni
 * `GRID_DAYS_ATTR`).
 *
 * Fa la mateixa forma que una fila d'habitació (columna de l'esquerra + dies) perquè la línia
 * d'avui caigui a la mateixa x i passi per sobre del separador.
 */
function SectionHeaderRow({
  label,
  loading,
  error,
  onRetry,
  empty,
  emptyLabel,
  todayLineLeft,
}: {
  label: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  empty: boolean;
  emptyLabel?: string;
  todayLineLeft: number | null;
}) {
  return (
    <div
      data-testid="section-header"
      className={cn('sticky flex border-b-2', Z_SECTION_HEADER)}
      style={{
        top: GRID_HEADER_HEIGHT,
        backgroundColor: SECTION_BG,
        borderBottomColor: CALENDAR_PALETTE.separator,
      }}
    >
      {/* Columna de l'esquerra (amplada de la de les habitacions); el text en surt per la dreta
          (no es talla als 72 px). */}
      <div
        className="sticky left-0 z-[1] flex shrink-0 items-center py-1.5"
        style={{ width: ROOM_COLUMN_WIDTH, minWidth: ROOM_COLUMN_WIDTH, backgroundColor: SECTION_BG }}
      >
        <div className="flex w-max max-w-[100vw] items-center gap-2 whitespace-nowrap px-3">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-700">{label}</span>
          {error ? (
            <span className="flex items-center gap-2 text-xs text-red-600" role="alert">
              Error carregant
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded border border-red-300 bg-white px-1.5 py-0.5 text-xs text-red-700 hover:bg-red-50 touch-manipulation"
                >
                  Reintentar
                </button>
              )}
            </span>
          ) : loading ? (
            <span className="flex items-center gap-1 text-xs text-blue-700">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Carregant...
            </span>
          ) : (
            empty && (
              <span className="text-xs text-gray-600">
                {emptyLabel ?? 'Sense habitacions configurades'}
              </span>
            )
          )}
        </div>
      </div>
      <div className="relative flex-1">
        {todayLineLeft !== null && <TodayLine left={todayLineLeft} className="top-0 -bottom-0.5" />}
      </div>
    </div>
  );
}

/** El fantasma d'una reserva que es mou, al lloc de l'original (`DragGhost`). */
function MoveGhost({
  reservation,
  range,
  cellWidth,
  slotStyle,
  collision,
  ghostRef,
}: {
  reservation: ReservationCellData | undefined;
  range: VisibleRange | null;
  cellWidth: number;
  slotStyle: RowSlotStyle;
  collision: boolean;
  ghostRef: React.Ref<HTMLDivElement>;
}) {
  if (!reservation || !range) return null;
  const pos = calculateReservationPosition(
    reservation.checkIn,
    reservation.checkOut,
    range,
    cellWidth,
  );
  if (!pos) return null;
  return (
    <DragGhost
      data={reservation}
      left={pos.left}
      width={pos.width}
      slotStyle={slotStyle}
      collision={collision}
      ghostRef={ghostRef}
    />
  );
}

export default CalendarGrid;
